import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { authForgotPassword, authResetPassword, authResendOtp } from '@/lib/api';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await authForgotPassword(email.trim());
      toast.success('If an account exists, a reset code has been sent to your email.');
      setStep('reset');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error('Please enter the reset code'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await authResetPassword(email.trim(), otp.trim(), newPassword);
      toast.success(res.data.message || 'Password reset successfully!');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      await authResendOtp(email.trim());
      toast.success('A new reset code has been sent to your email.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-[400px]">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo192.png" alt="Flowlancer" className="w-8 h-8 rounded-md" />
          <span className="font-display text-subhead text-ink">FlowLancer</span>
        </Link>

        <h1 className="mt-10 font-display text-display-l text-ink">Reset password</h1>
        <p className="mt-3 text-body text-ink-muted">
          {step === 'email'
            ? "Enter your email and we'll send you a reset code."
            : 'Enter the code sent to your email and your new password.'}
        </p>

        {step === 'email' ? (
          <form onSubmit={handleSendCode} className="mt-10 space-y-5">
            <Field label="Email address" htmlFor="email">
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </Field>
            <Button type="submit" disabled={loading} className="w-full h-11">
              {loading ? 'Sending…' : 'Send reset code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="mt-10 space-y-5">
            <Field label="Reset code" htmlFor="otp">
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                autoComplete="one-time-code"
                className="tabular-nums"
              />
            </Field>

            <Field label="New password" htmlFor="new-password" hint="Minimum 8 characters.">
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink"
                >
                  {showPassword
                    ? <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                    : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </div>
            </Field>

            <Field label="Confirm password" htmlFor="confirm-password">
              <Input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </Field>

            <Button type="submit" disabled={loading} className="w-full h-11">
              {loading ? 'Resetting…' : 'Reset password'}
            </Button>

            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resending}
              className="text-small text-forest hover:text-forest-hover disabled:opacity-40"
            >
              {resending ? 'Resending…' : "Didn't get the code? Resend"}
            </button>
          </form>
        )}

        <p className="mt-8">
          <Link to="/login" className="text-small text-forest hover:text-forest-hover inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
